/**
 * Geometry/appearance measurement for the render-based design-quality lint rules. The composition is
 * loaded in an off-screen iframe; this module seeks its timeline to a set of probe points and, at
 * each, records every rendered element's box, glyph region, effective opacity, and text appearance.
 * The runtime rules then consume these frame snapshots as pure data. Ported from the codeo layout
 * measurer.
 */

// Tags whose text/content is never rendered as on-screen copy.
const NON_RENDERED_TAGS = new Set([
    'SCRIPT',
    'STYLE',
    'TEMPLATE',
    'NOSCRIPT',
    'TITLE',
    'META',
    'LINK',
]);
const TEXT_NODE = 3;

export interface Rect {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface ElementMeasurement {
    /** Live element reference, so rules can read attributes / walk ancestors for opt-outs. */
    element: Element;
    /** Border-box in canvas pixel space. */
    rect: Rect;
    /** The glyph/content region (union of the element's text range rects), for overlap/contrast. */
    inkRect: Rect;
    /** Cumulative opacity down the ancestor chain (0..1). */
    opacity: number;
    /** Computed font-size in px. */
    fontSizePx: number;
    /** Computed foreground colour. */
    color: string;
    /** Whether the computed font-weight is bold (>= 600). */
    bold: boolean;
    /** Whether the element renders its own non-whitespace text directly. */
    hasText: boolean;
    /**
     * Where this element's own glyphs actually put ink, as opposed to where its boxes are. A line
     * box is only as tall as `line-height`, while the glyphs in it are drawn from the font's own
     * ascent and descent — at `line-height: 1` a descender hangs below the box it belongs to. Set
     * only for an element rendering its own copy, and only where the ink could be measured.
     */
    glyphRect?: Rect;
    /**
     * Where the copy's letterforms sit on, in the same space as `glyphRect`. The ink box runs to
     * the bottom of the deepest descender, so the band below this line is mostly empty — which is
     * exactly where an underline is drawn.
     */
    baselineY?: number;
    /**
     * What is left of the frame after every ancestor that clips overflow has had its say, or
     * undefined when nothing above this element clips. Anything the element draws outside this
     * region does not reach the viewer.
     */
    clipRect?: Rect;
    /**
     * Points sampled along SVG geometry, in canvas pixel space, one run per painted subpath. A
     * stroke's box says where it could be, not where it is: a rising curve's box covers the headline
     * above it while the line runs well beneath. Runs are split where the geometry jumps to a new
     * subpath, so the empty space between an icon's legs is not read as paint. Absent for anything
     * that is not SVG geometry.
     */
    outline?: [number, number][][];
}

export interface FrameSnapshot {
    /** Scene progress this frame was captured at (0..1) — see `RuntimeLintContext.seek`. */
    progress: number;
    elements: ElementMeasurement[];
    /** Rasterize this frame to a canvas on demand (re-seeks first). Used only by the contrast rule. */
    rasterize(): Promise<HTMLCanvasElement>;
}

/** The rendered canvas size, read from the loaded document's viewport. */
export function canvasSizeOf(document: Document): { width: number; height: number } {
    return {
        width: document.documentElement.clientWidth,
        height: document.documentElement.clientHeight,
    };
}

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

/**
 * The paint a text element's glyphs are actually drawn in. SVG text is painted with `fill` and
 * ignores the CSS `color` property, so measuring `color` on it reports an inherited default the
 * glyphs were never drawn in — and the contrast rule then fails readable labels and sends the author
 * to paint a scrim behind them. The computed value comes first, since that is what a stylesheet or a
 * `var()` resolves to; the authored attribute (its own, or inherited from the group it sits in) is
 * the fallback for an environment that does not resolve SVG presentation attributes.
 */
function textPaint(element: Element, style: CSSStyleDeclaration | undefined): string {
    if (element.namespaceURI !== SVG_NAMESPACE) {
        return style?.color ?? '';
    }
    const computed = style?.fill ?? '';
    if (computed && computed !== 'none') {
        return computed;
    }
    for (
        let current: Element | null = element;
        current && current.namespaceURI === SVG_NAMESPACE;
        current = current.parentElement
    ) {
        const inline = (current as SVGElement).style?.fill;
        if (inline) return inline;
        const attribute = current.getAttribute('fill');
        if (attribute) return attribute;
    }
    return style?.color ?? '';
}

/** Cumulative opacity: the product of the element's and every ancestor's computed opacity. */
export function effectiveOpacity(el: Element): number {
    const win = el.ownerDocument?.defaultView;
    let opacity = 1;
    let cur: Element | null = el;
    while (cur) {
        const raw =
            (win ? win.getComputedStyle(cur).opacity : '') ||
            (cur as HTMLElement).style?.opacity ||
            '';
        const v = parseFloat(raw);
        if (Number.isFinite(v)) opacity *= v;
        cur = cur.parentElement;
    }
    return opacity;
}

function toRect(box: { left: number; top: number; width: number; height: number }): Rect {
    return { x: box.left, y: box.top, width: box.width, height: box.height };
}

/** The region two boxes share, collapsed to nothing rather than inverted when they are disjoint. */
export function intersectRects(a: Rect, b: Rect): Rect {
    const x = Math.max(a.x, b.x);
    const y = Math.max(a.y, b.y);
    return {
        x,
        y,
        width: Math.max(0, Math.min(a.x + a.width, b.x + b.width) - x),
        height: Math.max(0, Math.min(a.y + a.height, b.y + b.height) - y),
    };
}

/**
 * Whether this box is the frame rather than a window inside it.
 *
 * Every composition wraps itself in a full-bleed stage carrying `overflow: hidden`, so almost
 * everything has a clipping ancestor. That stage is not a window and must not be treated as one:
 * clipping against it would put every element inside the canvas by construction and quietly retire
 * the rules that ask whether content has been left off-frame.
 */
export function coversCanvas(
    rect: Rect,
    canvas: { width: number; height: number } | undefined,
): boolean {
    if (!canvas) return false;
    const slack = 4;
    return (
        rect.x <= slack &&
        rect.y <= slack &&
        rect.x + rect.width >= canvas.width - slack &&
        rect.y + rect.height >= canvas.height - slack
    );
}

/**
 * The part of a box that a window inside the frame lets through.
 *
 * A wheel of digits is thirty digits tall and shows one; a feed is a long track behind a phone
 * screen. Measured whole, such a strip is off the canvas and across everything else on the frame,
 * and the rules that judge position would report all of it — which is what taught one composition
 * to carry three `data-l5-allow-*` waivers for a technique working exactly as intended.
 */
export function visibleThroughWindows(
    rect: Rect,
    clipRect: Rect | undefined,
    canvas: { width: number; height: number } | undefined,
): Rect {
    if (!clipRect || coversCanvas(clipRect, canvas)) return rect;
    return intersectRects(rect, clipRect);
}

/**
 * Every box above this element that crops what overflows it, innermost first.
 *
 * Any computed `overflow` other than `visible` crops: `auto` and `scroll` both clip in a frame that
 * cannot scroll, and Chrome rewrites a one-axis `overflow: visible hidden` to `auto hidden`, so
 * treating `auto` as a bleed would miss exactly the windows this matters for.
 */
/**
 * Whether this element is something an absolutely positioned descendant is laid out against.
 *
 * Anything positioned is, and so is a transform, filter or perspective, which trap even a fixed
 * descendant. The point of asking is that `overflow` crops a descendant only when the cropping box
 * is in that descendant's containing block chain.
 */
function establishesContainingBlock(style: CSSStyleDeclaration): boolean {
    if (style.position !== 'static') return true;
    return traps(style);
}

/** The properties that pin even a `position: fixed` descendant to an element. */
function traps(style: CSSStyleDeclaration): boolean {
    return ((style.transform && style.transform !== 'none') ||
        (style.filter && style.filter !== 'none') ||
        (style.perspective && style.perspective !== 'none')) as boolean;
}

/**
 * Every box above this element that crops what overflows it, innermost first.
 *
 * Any computed `overflow` other than `visible` crops: `auto` and `scroll` both clip in a frame that
 * cannot scroll, and Chrome rewrites a one-axis `overflow: visible hidden` to `auto hidden`, so
 * treating `auto` as a bleed would miss exactly the windows this matters for.
 *
 * An ancestor only crops what it is laid out against, though. Measured in Chrome: a static
 * `overflow: hidden` card 200px wide, holding an absolutely positioned label 900px wide, lets the
 * label run its full width — the label's containing block is further up, so the card never crops
 * it. Counting it would invent 700px of overflow on a layout that renders exactly as authored, so
 * the walk carries the position it is escaping with and skips the boxes that cannot hold it.
 */
export function clippingAncestorsOf(el: Element): Element[] {
    const view = el.ownerDocument?.defaultView;
    if (!view) return [];
    const clippers: Element[] = [];
    let escaping = view.getComputedStyle(el).position;
    for (let node = el.parentElement; node; node = node.parentElement) {
        const style = view.getComputedStyle(node);
        const holdsIt =
            escaping === 'absolute'
                ? establishesContainingBlock(style)
                : escaping === 'fixed'
                  ? traps(style)
                  : true;
        if (!holdsIt) continue;
        if (!/^visible/i.test((style.overflow || 'visible').trim())) clippers.push(node);
        escaping = style.position;
    }
    return clippers;
}

/**
 * What the clipping boxes above an element leave visible. The padding box is what `overflow` crops
 * to, so it is read off `clientLeft`/`clientWidth` rather than from the border box.
 */
/** An element's border box on screen, and the untransformed layout numbers describing its insets. */
export interface ClipperGeometry {
    box: Rect;
    clientLeft: number;
    clientTop: number;
    clientWidth: number;
    clientHeight: number;
    offsetWidth: number;
    offsetHeight: number;
}

/**
 * The padding box of a clipper, in the space its contents are measured in.
 *
 * `clientLeft`/`clientWidth` are untransformed layout values while the border box is where the
 * element actually lands, so under a scaled ancestor the two describe different rectangles. The
 * ratio between them carries the insets back into screen space; without it a phone screen tweened
 * to `scale: 2.85` keeps its authored 400×300 while sitting in a box nearly three times that, and
 * copy filling it reads as hundreds of pixels cut off.
 */
export function paddingBoxInScreenSpace(geometry: ClipperGeometry): Rect {
    const { box, offsetWidth, offsetHeight } = geometry;
    // `offsetWidth` is an HTMLElement property, so an `<svg>` — which the UA stylesheet gives
    // `overflow: hidden`, putting it in this walk — reports nothing to scale against while its
    // `clientWidth` stays in layout units. Scaling by 1 there would mix the very spaces this
    // function exists to reconcile, so its border box, which is already on screen, is the answer.
    if (offsetWidth <= 0 || offsetHeight <= 0) return box;
    const scaleX = box.width / offsetWidth;
    const scaleY = box.height / offsetHeight;
    return {
        x: box.x + geometry.clientLeft * scaleX,
        y: box.y + geometry.clientTop * scaleY,
        width: geometry.clientWidth ? geometry.clientWidth * scaleX : box.width,
        height: geometry.clientHeight ? geometry.clientHeight * scaleY : box.height,
    };
}

function clipRectOf(el: Element): Rect | undefined {
    let clip: Rect | undefined;
    for (const node of clippingAncestorsOf(el)) {
        const padding = paddingBoxInScreenSpace({
            box: toRect(node.getBoundingClientRect()),
            clientLeft: node.clientLeft,
            clientTop: node.clientTop,
            clientWidth: node.clientWidth,
            clientHeight: node.clientHeight,
            offsetWidth: (node as HTMLElement).offsetWidth || 0,
            offsetHeight: (node as HTMLElement).offsetHeight || 0,
        });
        clip = clip ? intersectRects(clip, padding) : padding;
    }
    return clip;
}

/**
 * One scratch canvas per document, since measuring text is the only thing it is for and a fresh one
 * per element would allocate a few hundred of them over a lint pass. Held weakly so a composition
 * that has been torn down does not keep its canvas alive.
 */
/**
 * A font no composition would ask for, set before each element so that a shorthand canvas rejects
 * leaves something recognisable behind. A plain `10px sans-serif` cannot do this job: canvas
 * normalises `normal 400 10px sans-serif` to exactly that string, so a real element asking for it
 * would read as a failure and be dropped from the clip check.
 */
const FONT_PROBE = '1px "l5-lint-probe"';

const textMetricsContexts = new WeakMap<Document, CanvasRenderingContext2D | null>();

function textMetricsContext(doc: Document): CanvasRenderingContext2D | null {
    const existing = textMetricsContexts.get(doc);
    if (existing !== undefined) return existing;
    const context = doc.createElement('canvas').getContext('2d');
    textMetricsContexts.set(doc, context);
    return context;
}

/**
 * The ink of the element's own copy, line by line.
 *
 * Each rect a Range reports for a line spans the font's ascent to its descent, so the baseline sits
 * a known distance below the rect's top and the glyphs can be placed against it. The vertical
 * extents come from the widest ascent and descent over the whole string, which is what a clipped
 * descender needs and does not depend on which characters fell on which line; the horizontal extent
 * is the line's own box. Returns undefined rather than a guess wherever the measurement is
 * unavailable — no canvas, no font, no text.
 */
function glyphRectOf(
    el: Element,
    style: CSSStyleDeclaration | undefined,
): { rect: Rect; baselineY: number } | undefined {
    const doc = el.ownerDocument;
    const view = doc?.defaultView;
    const text = ownElementText(el);
    if (!doc || !view || !style || !text) return undefined;
    let metrics: { ascent: number; descent: number };
    // The Range rect's top is the font's ascent above the baseline, so this is the offset from any
    // line's top down to its baseline.
    let baselineOffset: number;
    try {
        const context = textMetricsContext(doc);
        if (!context) return undefined;
        // Canvas ignores a font it cannot parse, and this context is shared, so without clearing it
        // first an unparseable shorthand would silently measure this element in the last one's face.
        context.font = FONT_PROBE;
        const probeTook = context.font;
        context.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        if (context.font === probeTook) return undefined;
        const measured = context.measureText(text);
        const probe = context.measureText('0');
        metrics = {
            ascent: measured.actualBoundingBoxAscent,
            descent: measured.actualBoundingBoxDescent,
        };
        baselineOffset = probe.fontBoundingBoxAscent;
        if (
            !Number.isFinite(metrics.ascent) ||
            !Number.isFinite(metrics.descent) ||
            !Number.isFinite(baselineOffset)
        ) {
            return undefined;
        }
    } catch {
        return undefined;
    }
    // One range per direct text node rather than one over the whole element: a range over the
    // contents also returns a rect for every descendant run, and a nested span set at another size
    // would then be measured against the parent's ascent and read as hanging far below its line.
    let lines: DOMRect[];
    try {
        lines = [];
        for (const node of Array.from(el.childNodes)) {
            if (node.nodeType !== TEXT_NODE) continue;
            if (!(node.textContent ?? '').trim()) continue;
            const range = doc.createRange();
            range.selectNodeContents(node);
            lines.push(...Array.from(range.getClientRects()));
            range.detach();
        }
    } catch {
        return undefined;
    }
    if (lines.length === 0) return undefined;
    let left = Infinity;
    let right = -Infinity;
    let top = Infinity;
    let bottom = -Infinity;
    // The lowest line's baseline, which is the one the ink box's descender depth hangs from.
    let baselineY = -Infinity;
    for (const line of lines) {
        const baseline = line.top + baselineOffset;
        left = Math.min(left, line.left);
        right = Math.max(right, line.right);
        top = Math.min(top, baseline - metrics.ascent);
        bottom = Math.max(bottom, baseline + metrics.descent);
        baselineY = Math.max(baselineY, baseline);
    }
    if (!Number.isFinite(left) || !Number.isFinite(top)) return undefined;
    return {
        rect: { x: left, y: top, width: right - left, height: bottom - top },
        baselineY,
    };
}

// The union of the element's border box and its text-range box, so a text element whose container
// is large still measures its actual glyph extent (the codeo getInkRect trick).
function inkRectOf(el: Element): Rect {
    const box = el.getBoundingClientRect();
    const doc = el.ownerDocument;
    if (!doc) return toRect(box);
    try {
        const range = doc.createRange();
        range.selectNodeContents(el);
        const rangeRect = range.getBoundingClientRect();
        range.detach();
        if (rangeRect.width === 0 && rangeRect.height === 0) return toRect(box);
        const left = Math.min(box.left, rangeRect.left);
        const top = Math.min(box.top, rangeRect.top);
        const right = Math.max(box.right, rangeRect.right);
        const bottom = Math.max(box.bottom, rangeRect.bottom);
        return { x: left, y: top, width: right - left, height: bottom - top };
    } catch {
        return toRect(box);
    }
}

/**
 * The copy this element renders *itself* — its direct text-node children only, whitespace-collapsed.
 * Descendant text belongs to the descendants, so a wrapper reads as having no copy of its own.
 */
export function ownElementText(el: Element): string {
    return Array.from(el.childNodes)
        .filter((node) => node.nodeType === TEXT_NODE)
        .map((node) => node.textContent ?? '')
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function rendersOwnText(el: Element): boolean {
    return ownElementText(el).length > 0;
}

// How much of an element's copy to quote when naming it in a finding.
const DESCRIBE_TEXT_MAX = 32;

/**
 * How a finding names the element it is about. An id is best, but plenty of composition elements
 * don't have one, and a bare `<span>` is unfindable in a document full of spans — an author faced
 * with one has to guess, and guessing costs a turn. So fall back to the first class name and a
 * snippet of the element's own copy, either of which can be searched for directly in the markup.
 *
 * Only the element's own text is quoted, never a descendant's: every element in the document gets
 * measured, so the geometry rules routinely report on wrappers, and borrowing a child's words would
 * send the author to the child instead of the element the finding is about.
 */
export function describeElement(element: Element): string {
    const tag = element.tagName.toLowerCase();
    if (element.id) {
        return `<${tag}#${element.id}>`;
    }
    // `className` is an SVGAnimatedString on SVG elements, so only trust it when it's a string.
    const firstClass =
        typeof element.className === 'string' ? element.className.trim().split(/\s+/)[0] : '';
    const selector = firstClass ? `<${tag}.${firstClass}>` : `<${tag}>`;
    const text = ownElementText(element);
    if (!text) {
        return selector;
    }
    const snippet = text.length > DESCRIBE_TEXT_MAX ? `${text.slice(0, DESCRIBE_TEXT_MAX)}…` : text;
    return `${selector} "${snippet}"`;
}

/** Measure every rendered element in the document at its current (already-seeked) frame. */
const OUTLINE_SAMPLES = 48;
// Within one subpath a chord between neighbouring samples cannot exceed the arc step between them;
// a longer chord is the jump to the next subpath, across space nothing is drawn on.
const SUBPATH_JUMP_FACTOR = 1.5;

interface SvgGeometryLike {
    getTotalLength(): number;
    getPointAtLength(length: number): { x: number; y: number };
    getScreenCTM(): { a: number; b: number; c: number; d: number; e: number; f: number } | null;
}

function hasSvgGeometry(element: Element): element is Element & SvgGeometryLike {
    const candidate = element as Partial<SvgGeometryLike>;
    return (
        typeof candidate.getTotalLength === 'function' &&
        typeof candidate.getPointAtLength === 'function' &&
        typeof candidate.getScreenCTM === 'function'
    );
}

/**
 * Evenly spaced points along the element's geometry, mapped through its CTM to canvas pixels and
 * grouped into runs, one per subpath.
 */
function outlineOf(element: Element): [number, number][][] | undefined {
    if (!hasSvgGeometry(element)) return undefined;
    let length: number;
    let ctm: ReturnType<SvgGeometryLike['getScreenCTM']>;
    try {
        length = element.getTotalLength();
        ctm = element.getScreenCTM();
    } catch {
        return undefined;
    }
    if (!ctm || !Number.isFinite(length) || length <= 0) return undefined;
    const step = length / OUTLINE_SAMPLES;
    const scale = Math.hypot(ctm.a, ctm.b) || 1;
    const maxChord = step * scale * SUBPATH_JUMP_FACTOR;
    const runs: [number, number][][] = [];
    let run: [number, number][] = [];
    for (let k = 0; k <= OUTLINE_SAMPLES; k++) {
        const { x, y } = element.getPointAtLength(step * k);
        const point: [number, number] = [
            ctm.a * x + ctm.c * y + ctm.e,
            ctm.b * x + ctm.d * y + ctm.f,
        ];
        const previous = run[run.length - 1];
        if (previous && Math.hypot(point[0] - previous[0], point[1] - previous[1]) > maxChord) {
            runs.push(run);
            run = [];
        }
        run.push(point);
    }
    runs.push(run);
    return runs;
}

export function measureElements(document: Document): ElementMeasurement[] {
    const body = document.body;
    if (!body) return [];
    const view = document.defaultView;
    const measurements: ElementMeasurement[] = [];
    for (const element of Array.from(body.querySelectorAll('*'))) {
        if (NON_RENDERED_TAGS.has(element.tagName) || element.tagName === 'BR') continue;
        const style = view?.getComputedStyle(element);
        const measuredGlyphs = glyphRectOf(element, style ?? undefined);
        const fontSizePx = parseFloat(style?.fontSize ?? '') || 0;
        const weight = parseInt(style?.fontWeight ?? '', 10);
        measurements.push({
            element,
            rect: toRect(element.getBoundingClientRect()),
            inkRect: inkRectOf(element),
            opacity: effectiveOpacity(element),
            fontSizePx,
            color: textPaint(element, style ?? undefined),
            bold: Number.isFinite(weight) && weight >= 600,
            hasText: rendersOwnText(element),
            glyphRect: measuredGlyphs?.rect,
            baselineY: measuredGlyphs?.baselineY,
            clipRect: clipRectOf(element),
            outline: outlineOf(element),
        });
    }
    return measurements;
}

/**
 * The surface this module needs from a loaded composition: its document, a seek, and a rasterize.
 * Declared structurally rather than imported, so it is satisfied by the handle `introspectComposition`
 * hands out and by a plain fake in tests.
 */
export interface FrameSource {
    readonly document: Document;
    seek(progress: number): Document | Promise<Document | void> | void;
    rasterize(scale: number): Promise<HTMLCanvasElement>;
}

// The last probe sits near the end rather than at 1.0: content that only goes wrong in the final
// stretch of the timeline needs measuring, but the very last frame of a composition is often a
// degenerate pose (everything faded out) that tells the rules nothing.
export const DEFAULT_PROBE_POINTS = [0.06, 0.25, 0.5, 0.75, 0.94];

/**
 * Re-points measurements taken on a posed copy at the matching elements of `reference`. A sandboxed
 * luminary hands back a fresh copy at every pose, and the rules follow an element from frame to
 * frame by identity: across copies each frame's elements were strangers, so a chart building bar by
 * bar was judged wrong at every frame on its own, and one defect was reported once per probe.
 * Elements are paired by position only when both documents hold the same elements in the same
 * order; a copy that differs keeps its own, since pairing across a shifted list would measure one
 * element's box against another's.
 */
function asElementsOf(
    reference: Document,
    posed: Document,
    measurements: ElementMeasurement[],
): ElementMeasurement[] {
    if (posed === reference) return measurements;
    const own = Array.from(reference.body?.querySelectorAll('*') ?? []);
    const copied = Array.from(posed.body?.querySelectorAll('*') ?? []);
    const sameShape =
        own.length === copied.length &&
        own.every((el, i) => el.tagName === copied[i].tagName && el.id === copied[i].id);
    if (!sameShape) return measurements;
    const counterpart = new Map(copied.map((el, i) => [el, own[i]]));
    return measurements.map((m) => ({ ...m, element: counterpart.get(m.element) ?? m.element }));
}

/**
 * Seek the composition to each of the given probe points and capture a measured snapshot at each.
 * `rasterize` on a snapshot re-seeks to that frame before rasterizing, so lazily rasterizing one
 * frame is unaffected by later seeks. Every probe is measured; the caller chooses the probes and
 * passes a single one for a composition with no seekable timeline (where every probe would otherwise
 * measure the same static frame).
 */
export async function captureFrames(
    source: FrameSource,
    probes: number[] = DEFAULT_PROBE_POINTS,
): Promise<FrameSnapshot[]> {
    const poses: { progress: number; posed: Document; elements: ElementMeasurement[] }[] = [];
    for (const progress of probes) {
        const posed = (await source.seek(progress)) ?? source.document;
        poses.push({ progress, posed, elements: measureElements(posed) });
    }
    // The last copy posed is what `source.document` answers with once capture is done, so it is the
    // document the rules are handed alongside these frames.
    const reference = poses.at(-1)?.posed ?? source.document;
    return poses.map(({ progress, posed, elements }) => ({
        progress,
        elements: asElementsOf(reference, posed, elements),
        rasterize: async () => {
            await source.seek(progress);
            return source.rasterize(1);
        },
    }));
}
